import Head from "next/head";
import { GetStaticPropsResult } from "next";
import Image from "next/image";
import Link from "next/link";
import classNames from "classnames";
import { DrupalFile, DrupalMedia, DrupalNode, DrupalTaxonomyTerm, JsonApiErrors } from "next-drupal";
import { drupal } from "lib/drupal";

import { getGlobalElements } from "lib/get-global-elements";
import { Meta, MetaProps } from "components/meta";
import { useTranslation } from "next-i18next";
import { absoluteURL, formatDate } from "lib/utils";
import cn from 'classnames';

import { Layout, LayoutProps } from "components/layout";
import { NodeArticleTeaser } from "components/node--article--teaser";
import siteConfig from "site.config";

interface IndexPageProps {
  nodes: DrupalNode[];
  seminaires: DrupalTaxonomyTerm[];
  menus: any;
  blocks: any;
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + "...";
}

export default function IndexPage({ nodes, menus, blocks, seminaires }: IndexPageProps) {
  const { t } = useTranslation();

  return (
    <Layout meta={{ title: t("Comité d'histoire de la politique de la ville") }} menus={menus} blocks={blocks}>
      <div className="max-w-screen-2xl mx-auto container ">
        <div className="grid grid-cols-12 gap-4">



          {/* Contenu principal */}
           {/* Menu de navigation à droite */}
  <div className="col-span-12 pl-12  sticky top-0 bg-white z-20 shadow-md">
  <h3 className="text-2xl font-semibold text-gray-900">Sommaire</h3>
  <ul className="space-y-4 mb-4">
    {seminaires.map((seminaire) => (
      <li className="inline-block pr-4" key={seminaire.id}>
        <a
          href={`#seminaire-${seminaire.id}`}
          className="font-medium text-gray-600 hover:text-gray-950"
        >
          {seminaire.name}
        </a>
      </li>
    ))}
  </ul>
</div>
  
  <div className="col-span-12 ">
            {seminaires.map((seminaire) => (
              <div key={seminaire.id} className="bg-white mb-12">
<section
  id={`seminaire-${seminaire.id}`}
  className="relative scroll-mt-16"
  aria-labelledby={`seminaire-${seminaire.id}`}
>
                <div aria-hidden="true" className="relative">
                  {seminaire.field_media_image && (

                    <Image
                    width={500}
        height={300}
                      alt="seminaire.field_media_image.field_media_image.uri.url"
                      src={absoluteURL(seminaire.field_media_image.field_media_image.uri.url)}
          className="aspect-[3/2] w-full object-cover sm:aspect-[5/2] lg:absolute lg:aspect-auto lg:h-full lg:w-1/2 lg:pr-4 xl:pr-16"
                    />
                  )}
        <div className="mx-auto max-w-2xl px-4 pb-24 pt-16 sm:px-6 sm:pb-32 lg:grid lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8 lg:pt-32">
        

                    <div className="lg:col-start-2">

                      <h2
                        id={`seminaire-${seminaire.id}`}
                        className="mt-4 text-4xl font-bold tracking-tight text-gray-900"
                      >
    <Link
                            href=     {seminaire.path.alias}
                            passHref
                            className="rounded-md hover:bg-gray-50"

                          >
                 {seminaire.name}
                          </Link>
                     
                   
                      </h2>
    <Link
                            href=     {seminaire.path.alias}
                            passHref
                          

                          >
                      {seminaire.description && (
                        <div
                          dangerouslySetInnerHTML={{ __html: seminaire.description.value }}
                          className="mt-4 text-gray-500 rounded-md hover:bg-gray-50"
                        />
                      )}
                   </Link>

{nodes.filter((semin) => 
  semin.field_seminaire.id.includes(seminaire.id) && semin.field_seminaire.id !== "73f4f286-4a65-4336-a646-7a75caf24520"
).length > 0 && (
  <span className="font-bold mt-10 mb-10 w-full block text-center text-gray-900"> Séances </span>
)}






                    <dl className=" grid grid-cols-1 gap-x-8 gap-y-10 text-sm sm:grid-cols-2">
                      {nodes
                        .filter((semin) => semin.field_seminaire.id.includes(seminaire.id))
                        .map((filtersemin, index) => (
                          <Link
                            href={filtersemin.path.alias}
                            passHref
                            key={filtersemin.id}
                            className="rounded-md p-5 gap-x-16 gap-y-10 hover:bg-gray-50"
                          >
                            <dt className="font-medium text-gray-900">{filtersemin.title}</dt>
                            <dd className="mt-2 text-gray-500">


                            </dd>
                          </Link>
                        ))}
                    </dl>
                
                </div>
                </div>
                        </div>
                </section>
              </div>

            ))}
          </div>
       
        </div>
      </div>
    </Layout>
  );
}

export async function getStaticProps(context): Promise<GetStaticPropsResult<IndexPageProps>> {
  const seminaires = await drupal.getResourceCollectionFromContext<DrupalTaxonomyTerm[]>(
    "taxonomy_term--seminaires",
    context,
    {
      params: {
        fields: {
          "taxonomy_term--seminaires": "id,path,weight,name,description,field_media_image",
        },
        include: "field_media_image.field_media_image",
        sort: "weight", // Tri par ordre de poids
      },
    }
  );

  const nodes = await drupal.getResourceCollectionFromContext<DrupalNode[]>(
    "node--article",
    context,
    {
      params: {
        "filter[field_seminaire]": {
          condition: {
            path: "field_seminaire",
            operator: "IS NOT NULL",
          },
        },
        include: "field_media_image.field_media_image,uid",
        sort: "-created", // Tri par date de création
      },
    }
  );

  return {
    props: {
      ...(await getGlobalElements(context)),
      nodes,
      seminaires,
    },
    revalidate: 5, // Mise en cache pour 5 secondes
  };
}
